import React, { useEffect, useState, useRef } from 'react';
interface DraggableDesignProps {
  designId: string;
  imageUrl: string;
  scale: number;
  initialX?: number;
  initialY?: number;
  isSelected: boolean;
  zIndex: number;
  onSelect: (id: string) => void;
  onPositionChange: (x: number, y: number) => void;
  garmentBounds: DOMRect | null;
}
export function DraggableDesign({
  designId,
  imageUrl,
  scale,
  initialX = 0,
  initialY = 0,
  isSelected,
  zIndex,
  onSelect,
  onPositionChange,
  garmentBounds
}: DraggableDesignProps) {
  const [position, setPosition] = useState({
    x: initialX,
    y: initialY
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [designSize, setDesignSize] = useState({
    width: 0,
    height: 0
  });
  const dragStartPos = useRef({
    x: 0,
    y: 0
  });
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setPosition({
      x: initialX,
      y: initialY
    });
  }, [initialX, initialY]);
  // Track design image size
  useEffect(() => {
    if (imgRef.current) {
      const updateSize = () => {
        if (imgRef.current) {
          setDesignSize({
            width: imgRef.current.offsetWidth,
            height: imgRef.current.offsetHeight
          });
        }
      };
      if (imgRef.current.complete) {
        updateSize();
      } else {
        imgRef.current.addEventListener('load', updateSize);
      }
      return () => {
        if (imgRef.current) {
          imgRef.current.removeEventListener('load', updateSize);
        }
      };
    }
  }, [imageUrl]);
  const clampPosition = (x: number, y: number) => {
    if (!garmentBounds || !designSize.width || !designSize.height) {
      return {
        x,
        y
      };
    }
    // Account for scale when calculating bounds
    const scaledWidth = designSize.width * scale;
    const scaledHeight = designSize.height * scale;
    // Calculate bounds - designs are positioned from center
    const halfWidth = scaledWidth / 2;
    const halfHeight = scaledHeight / 2;
    const garmentHalfWidth = garmentBounds.width / 2;
    const garmentHalfHeight = garmentBounds.height / 2;
    const maxX = garmentHalfWidth - halfWidth;
    const minX = -garmentHalfWidth + halfWidth;
    const maxY = garmentHalfHeight - halfHeight;
    const minY = -garmentHalfHeight + halfHeight;
    return {
      x: Math.max(minX, Math.min(maxX, x)),
      y: Math.max(minY, Math.min(maxY, y))
    };
  };
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(designId);
    setIsDragging(true);
    dragStartPos.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        e.preventDefault();
        const newX = e.clientX - dragStartPos.current.x;
        const newY = e.clientY - dragStartPos.current.y;
        const clampedPos = clampPosition(newX, newY);
        setPosition(clampedPos);
        onPositionChange(clampedPos.x, clampedPos.y);
      }
    };
    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
      }
    };
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, garmentBounds, designSize, scale]);
  const showFrame = (isHovered || isSelected) && !isDragging;
  return <div ref={containerRef} className={`absolute top-1/2 left-1/2 select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`} style={{
    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
    transformOrigin: 'center',
    zIndex: 100 + zIndex
  }} onMouseDown={handleMouseDown} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-83">
      <img ref={imgRef} src={imageUrl} alt="Design" className="max-w-[200px] pointer-events-none" draggable={false} data-id="element-84" />

      {showFrame && <div className={`absolute inset-0 border-2 rounded pointer-events-none transition-opacity duration-150 ${isSelected ? 'border-blue-500 border-solid' : 'border-dashed border-blue-400'}`} data-id="element-85" />}
    </div>;
}